import { useTranslation } from 'react-i18next';
import { boats } from '../../../mocks/boats';
import { cars } from '../../../mocks/cars';
import { campers } from '../../../mocks/campers';
import { motorcycles } from '../../../mocks/motorcycles';
import { houses } from '../../../mocks/houses';
import ImageGallery from './ImageGallery';

export function BoatCard({ boat }: { boat: typeof boats[0] }) {
  const { t } = useTranslation();
  return (
    <div className="group bg-white rounded-2xl overflow-hidden border border-sand-200 hover:border-ocean-300 transition-all duration-300" data-product-shop>
      <div className="relative w-full h-72 overflow-hidden">
        <ImageGallery images={boat.gallery || [boat.image]} alt={`${boat.name} ${boat.model}`} />
        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full z-10">
          <span className="text-xs font-semibold text-ocean-500">{boat.type}</span>
        </div>
        <div className="absolute top-4 right-14 bg-navy-800/90 px-3 py-1.5 rounded-full z-10">
          <span className="text-xs font-semibold text-white">{t('boats.upTo')} {boat.capacity} {t('boats.guests')}</span>
        </div>
        {boat.skipperRequired && (
          <div className="absolute bottom-10 left-4 bg-coral-500 px-3 py-1.5 rounded-full z-10">
            <span className="text-xs font-semibold text-white flex items-center">
              <i className="ri-user-star-line mr-1"></i>{t('boats.skipperIncluded')}
            </span>
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="text-xl font-bold text-navy-800 mb-0.5">{boat.name}</h3>
        <p className="text-ocean-500 font-medium text-sm mb-4">{boat.model}</p>
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="flex items-center text-xs text-navy-600"><i className="ri-ruler-line mr-1.5 text-ocean-400"></i>{boat.length} × {boat.width}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-group-line mr-1.5 text-ocean-400"></i>{boat.capacity} {t('boats.passengers')}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-hotel-bed-line mr-1.5 text-ocean-400"></i>{boat.berths} {t('boats.berths')}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-dashboard-3-line mr-1.5 text-ocean-400"></i>{boat.engine}</div>
        </div>
        <p className="text-navy-600 text-sm mb-4 leading-relaxed line-clamp-2">{boat.description}</p>
        <div className="flex flex-wrap gap-1.5 mb-5">
          {boat.features.slice(0, 5).map((f, i) => (
            <span key={i} className="px-2.5 py-1 bg-ocean-50 text-ocean-700 text-xs font-medium rounded-full">{f}</span>
          ))}
        </div>
        <div className="bg-sand-50 rounded-xl p-3 mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-navy-500">{t('boats.startingFrom')}</p>
            <p className="text-2xl font-bold text-navy-800">&euro;{boat.pricePerDay}</p>
            <p className="text-xs text-navy-500">{t('boats.perDay')}</p>
          </div>
          <a href="#contact" className="px-5 py-2.5 bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700 text-white text-sm font-medium rounded-lg transition-all whitespace-nowrap cursor-pointer">
            {t('boats.bookThisBoat')}
          </a>
        </div>
      </div>
    </div>
  );
}

export function CarCard({ car }: { car: typeof cars[0] }) {
  const { t } = useTranslation();
  return (
    <div className="group bg-white rounded-2xl overflow-hidden border border-sand-200 hover:border-ocean-300 transition-all duration-300" data-product-shop>
      <div className="relative w-full h-72 overflow-hidden">
        <ImageGallery images={car.gallery || [car.image]} alt={`${car.name} ${car.model}`} />
        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full z-10">
          <span className="text-xs font-semibold text-ocean-500">{car.type}</span>
        </div>
        <div className="absolute top-4 right-14 bg-navy-800/90 px-3 py-1.5 rounded-full z-10">
          <span className="text-xs font-semibold text-white">{car.seats} {t('cars.seats')}</span>
        </div>
      </div>
      <div className="p-5">
        <h3 className="text-xl font-bold text-navy-800 mb-0.5">{car.name}</h3>
        <p className="text-ocean-500 font-medium text-sm mb-4">{car.model}</p>
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="flex items-center text-xs text-navy-600"><i className="ri-steering-2-line mr-1.5 text-ocean-400"></i>{car.transmission}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-group-line mr-1.5 text-ocean-400"></i>{car.seats} {t('cars.places')}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-door-open-line mr-1.5 text-ocean-400"></i>{car.doors} {t('cars.doors')}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-speed-up-line mr-1.5 text-ocean-400"></i>{car.engine}</div>
        </div>
        <p className="text-navy-600 text-sm mb-4 leading-relaxed line-clamp-2">{car.description}</p>
        <div className="flex flex-wrap gap-1.5 mb-5">
          {car.features.map((f, i) => (
            <span key={i} className="px-2.5 py-1 bg-ocean-50 text-ocean-700 text-xs font-medium rounded-full">{f}</span>
          ))}
        </div>
        <div className="bg-sand-50 rounded-xl p-3 mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-navy-500">{t('cars.startingFrom')}</p>
            <p className="text-2xl font-bold text-navy-800">&euro;{car.pricePerDay}</p>
            <p className="text-xs text-navy-500">{t('cars.perDay')}</p>
          </div>
          <a href="#contact" className="px-5 py-2.5 bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700 text-white text-sm font-medium rounded-lg transition-all whitespace-nowrap cursor-pointer">
            {t('cars.bookThisCar')}
          </a>
        </div>
      </div>
    </div>
  );
}

export function CamperCard({ camper }: { camper: typeof campers[0] }) {
  const { t } = useTranslation();
  return (
    <div className="group bg-white rounded-2xl overflow-hidden border border-sand-200 hover:border-ocean-300 transition-all duration-300" data-product-shop>
      <div className="relative w-full h-72 overflow-hidden">
        <ImageGallery images={(camper as any).gallery || [camper.image]} alt={`${camper.name} ${camper.model}`} />
        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full z-10">
          <span className="text-xs font-semibold text-ocean-500">{camper.type}</span>
        </div>
        <div className="absolute top-4 right-14 bg-navy-800/90 px-3 py-1.5 rounded-full z-10">
          <span className="text-xs font-semibold text-white">{camper.berths} {t('campers.berths')}</span>
        </div>
      </div>
      <div className="p-5">
        <h3 className="text-xl font-bold text-navy-800 mb-0.5">{camper.name}</h3>
        <p className="text-ocean-500 font-medium text-sm mb-4">{camper.model}</p>
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="flex items-center text-xs text-navy-600"><i className="ri-ruler-line mr-1.5 text-ocean-400"></i>{camper.length}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-group-line mr-1.5 text-ocean-400"></i>{camper.seats} {t('campers.seats')}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-steering-2-line mr-1.5 text-ocean-400"></i>{camper.transmission}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-speed-up-line mr-1.5 text-ocean-400"></i>{camper.engine}</div>
        </div>
        <p className="text-navy-600 text-sm mb-4 leading-relaxed line-clamp-2">{camper.description}</p>
        <div className="flex flex-wrap gap-1.5 mb-5">
          {camper.features.map((f, i) => (
            <span key={i} className="px-2.5 py-1 bg-ocean-50 text-ocean-700 text-xs font-medium rounded-full">{f}</span>
          ))}
        </div>
        <div className="bg-sand-50 rounded-xl p-3 mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-navy-500">{t('campers.startingFrom')}</p>
            <p className="text-2xl font-bold text-navy-800">&euro;{camper.pricePerDay}</p>
            <p className="text-xs text-navy-500">{t('campers.perDay')}</p>
          </div>
          <a href="#contact" className="px-5 py-2.5 bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700 text-white text-sm font-medium rounded-lg transition-all whitespace-nowrap cursor-pointer">
            {t('campers.bookThisCamper')}
          </a>
        </div>
      </div>
    </div>
  );
}

export function MotoCard({ moto }: { moto: typeof motorcycles[0] }) {
  const { t } = useTranslation();
  return (
    <div className="group bg-white rounded-2xl overflow-hidden border border-sand-200 hover:border-ocean-300 transition-all duration-300" data-product-shop>
      <div className="relative w-full h-72 overflow-hidden">
        <ImageGallery images={moto.gallery || [moto.image]} alt={`${moto.name} ${moto.model}`} />
        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full z-10">
          <span className="text-xs font-semibold text-ocean-500">{moto.type}</span>
        </div>
        <div className="absolute top-4 right-14 bg-navy-800/90 px-3 py-1.5 rounded-full z-10">
          <span className="text-xs font-semibold text-white">{moto.seats} {t('motorcycles.seats')}</span>
        </div>
      </div>
      <div className="p-5">
        <h3 className="text-xl font-bold text-navy-800 mb-0.5">{moto.name}</h3>
        <p className="text-ocean-500 font-medium text-sm mb-4">{moto.model}</p>
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="flex items-center text-xs text-navy-600"><i className="ri-settings-3-line mr-1.5 text-ocean-400"></i>{moto.engine}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-flashlight-line mr-1.5 text-ocean-400"></i>{moto.power}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-steering-2-line mr-1.5 text-ocean-400"></i>{moto.transmission}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-group-line mr-1.5 text-ocean-400"></i>{moto.seats} {t('motorcycles.seats')}</div>
        </div>
        <p className="text-navy-600 text-sm mb-4 leading-relaxed line-clamp-2">{moto.description}</p>
        <div className="flex flex-wrap gap-1.5 mb-5">
          {moto.features.map((f, i) => (
            <span key={i} className="px-2.5 py-1 bg-ocean-50 text-ocean-700 text-xs font-medium rounded-full">{f}</span>
          ))}
        </div>
        <div className="bg-sand-50 rounded-xl p-3 mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-navy-500">{t('motorcycles.startingFrom')}</p>
            <p className="text-2xl font-bold text-navy-800">&euro;{moto.pricePerDay}</p>
            <p className="text-xs text-navy-500">{t('motorcycles.perDay')}</p>
          </div>
          <a href="#contact" className="px-5 py-2.5 bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700 text-white text-sm font-medium rounded-lg transition-all whitespace-nowrap cursor-pointer">
            {t('motorcycles.bookThisMotorcycle')}
          </a>
        </div>
      </div>
    </div>
  );
}

export function HouseCard({ house }: { house: typeof houses[0] }) {
  const { t } = useTranslation();
  return (
    <div className="group bg-white rounded-2xl overflow-hidden border border-sand-200 hover:border-ocean-300 transition-all duration-300" data-product-shop>
      <div className="relative w-full h-72 overflow-hidden">
        <ImageGallery images={house.gallery || [house.image]} alt={`${house.name} - ${house.type}`} />
        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full z-10">
          <span className="text-xs font-semibold text-ocean-500">{house.type}</span>
        </div>
        <div className="absolute top-4 right-14 bg-navy-800/90 px-3 py-1.5 rounded-full z-10">
          <span className="text-xs font-semibold text-white">{t('houses.upTo')} {house.guests} {t('houses.guests')}</span>
        </div>
        <div className="absolute bottom-10 left-4 bg-turquoise-500/90 px-3 py-1.5 rounded-full z-10">
          <span className="text-xs font-semibold text-white flex items-center">
            <i className="ri-map-pin-line mr-1"></i>{house.location}
          </span>
        </div>
      </div>
      <div className="p-5">
        <h3 className="text-xl font-bold text-navy-800 mb-0.5">{house.name}</h3>
        <p className="text-ocean-500 font-medium text-sm mb-4">{house.model}</p>
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="flex items-center text-xs text-navy-600"><i className="ri-hotel-bed-line mr-1.5 text-ocean-400"></i>{house.bedrooms} {t('houses.bedrooms')}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-group-line mr-1.5 text-ocean-400"></i>{house.guests} {t('houses.guests')}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-drop-line mr-1.5 text-ocean-400"></i>{house.bathrooms} {t('houses.bathrooms')}</div>
          <div className="flex items-center text-xs text-navy-600"><i className="ri-layout-2-line mr-1.5 text-ocean-400"></i>{house.area}</div>
        </div>
        <p className="text-navy-600 text-sm mb-4 leading-relaxed line-clamp-2">{house.description}</p>
        <div className="flex flex-wrap gap-1.5 mb-5">
          {house.features.slice(0, 5).map((f, i) => (
            <span key={i} className="px-2.5 py-1 bg-ocean-50 text-ocean-700 text-xs font-medium rounded-full">{f}</span>
          ))}
        </div>
        <div className="bg-sand-50 rounded-xl p-3 mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-navy-500">{t('houses.startingFrom')}</p>
            <p className="text-2xl font-bold text-navy-800">&euro;{house.pricePerDay}</p>
            <p className="text-xs text-navy-500">{t('houses.perDay')}</p>
          </div>
          <a href="#contact" className="px-5 py-2.5 bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700 text-white text-sm font-medium rounded-lg transition-all whitespace-nowrap cursor-pointer">
            {t('houses.bookThisHouse')}
          </a>
        </div>
      </div>
    </div>
  );
}
